<template>
  <GenericCard
    :locked="locked"
    has-delete
    :on-delete="onCardDelete"
    :on-action="submitForm"
    :on-refresh="onCardRefresh"
    :invalid="v$.section.$invalid"
    :dirty="v$.section.$anyDirty"
    :create="create"
    title="Detention History"
    :section="section"
    has-footer
    has-share
    :card-identifier="cardIdentifier"
    :survey-validation="surveyValidation"
    :survey-identifier="surveyIdentifier"
  >
    <v-form
      :disabled="locked"
      @submit.prevent="submitForm"
    >
      <v-row>
        <v-col cols="3">
          <v-autocomplete
            v-model="section.authority"
            :items="optionsAuthorities"
            label="Statutory Authority"
            outlined
            :error-messages="getErrorMessages(v$.section.authority)"
            @input="v$.section.authority.$touch()"
            @blur="v$.section.authority.$touch()"
          />
        </v-col>
        <v-col>
          <v-text-field
            v-model="section.facility"
            autocomplete="off"
            label="Facility"
            outlined
            :error-messages="getErrorMessages(v$.section.facility)"
            @input="v$.section.facility.$touch()"
            @blur="v$.section.facility.$touch()"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col
          cols="6"
        >
          <FormatSelectableComboDate
            class="mt-3 ml-1"
            label="Issue Date"
            :formatable-date="section.initiated_date"
            :format="section.initiated_date_format"
            :uncertain="section.initiated_date_uncertain"
            required
            outlined
            :disabled="locked"
            for-party-edit
            :error-messages="getErrorMessages(v$.section.initiated_date)"
            @update-box="onInitiatedDateUncertainUpdate"
            @update-date="onInitiatedDateUpdate"
            @tab-out="v$.section.initiated_date.$touch()"
          />
        </v-col>
        <v-col
          cols="6"
        >
          <FormatSelectableComboDate
            class="mt-3 ml-1"
            label="Terminated Date"
            :formatable-date="section.terminated_date"
            :format="section.terminated_date_format"
            :uncertain="section.terminated_date_uncertain"
            required
            outlined
            :disabled="locked"
            for-party-edit
            :error-messages="getErrorMessages(v$.section.terminated_date)"
            @update-box="onTerminatedDateUncertainUpdate"
            @update-date="onTerminatedDateUpdate"
            @tab-out="v$.section.terminated_date.$touch()"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-autocomplete
            v-model="section.reason"
            :items="optionsReasons"
            label="Reason Released"
            outlined
            :error-messages="getErrorMessages(v$.section.reason)"
            @input="v$.section.reason.$touch()"
            @blur="v$.section.reason.$touch()"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="9">
          <v-autocomplete
            v-model="section.conditions"
            :items="optionsConditions"
            label="Conditions of Release"
            outlined
            multiple
            :error-messages="getErrorMessages(v$.section.conditions)"
            @input="v$.section.conditions.$touch()"
            @blur="v$.section.conditions.$touch()"
          />
        </v-col>
        <v-col v-if="showBmt">
          <v-text-field
            v-model="section.bond_amt"
            autocomplete="off"
            label="Bond Amount"
            outlined
            prefix="$"
            type="number"
            :error-messages="getErrorMessages(v$.section.bond_amt)"
            @input="v$.section.bond_amt.$touch()"
            @blur="v$.section.bond_amt.$touch()"
          />
        </v-col>
      </v-row>
      <v-row>
        <!-- text-area notes -->
        <v-col>
          <v-textarea
            v-model="section.notes"
            label="Notes"
            rows="4"
            outlined
            :error-messages="getErrorMessages(v$.section.notes)"
            @input="v$.section.notes.$touch()"
            @blur="v$.section.notes.$touch()"
          />
        </v-col>
      </v-row>
    </v-form>
  </GenericCard>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'

import { useVuelidate } from '@vuelidate/core'

import { PARTY_NAMESPACE } from '@/store/party'
import { SHARED_NAMESPACE } from '@/store/shared'

import { alertHandler } from '@/alertHandler'

import FormatSelectableComboDate from '@/components/Fields/FormatSelectableComboDate'
import GenericCard from '@/components/Party/PartyEdit/SectionCards/GenericCard'

export default {
  name: 'DetentionHistoriesCard',
  components: { FormatSelectableComboDate, GenericCard },

  props: {
    item: {
      type: Object,
      required: true,
    },

    surveyIdentifier: {
      type: String,
      default: null,
    },

    surveyValidation: {
      type: Boolean,
      default: false,
    },
  },

  setup () {
    return {
      v$: useVuelidate(),
      ...alertHandler(),
    }
  },

  data () {
    return {
      section: {},
    }
  },

  validations () {
    return {
      section: {
        authority: {},
        bond_amt: {},
        conditions: {},
        facility: {},
        initiated_date: {},
        initiated_date_uncertain: {},
        notes: {},
        reason: {},
        terminated_date: {},
        terminated_date_uncertain: {},
      },
    }
  },

  computed: {
    cardIdentifier () { return `detention-history-card-${this.sectionIdentifier}` },
    create () { return this.item.id === null },

    locked: {
      get () {
        return this.peDetentionHistoriesCardLock(this.sectionIdentifier)
      },

      set (value) {
        this.setPeDetentionHistoriesCardLock({ index: this.sectionIdentifier, locked: value })
      },
    },

    optionsAuthorities () {
      return this.getCachedDropdown('detention_histories/authority')
    },

    optionsConditions () {
      return this.getCachedDropdown('detention_histories/conditions')
    },

    optionsReasons () {
      return this.getCachedDropdown('detention_histories/reason')
    },

    sectionIdentifier () { return this.section.id || 'new' },
    showBmt () { return this.section.conditions?.includes('bond') },

    ...mapGetters(PARTY_NAMESPACE, [
      'peDetentionHistoriesCardLock',
    ]),

    ...mapGetters(SHARED_NAMESPACE, [
      'getCachedDropdown',
    ]),
  },

  watch: {
    item: {
      immediate: true,

      handler: function () {
        this.section = { ...this.item }
      },

      deep: true,
    },
  },

  created () {
    if (!this.create) this.initPeDetentionHistoriesCard(this.sectionIdentifier)

    this.getDropdownOptions({ path: 'detention_histories/authority' })
    this.getDropdownOptions({ path: 'detention_histories/reason' })
    this.getDropdownOptions({ path: 'detention_histories/conditions' })
  },

  methods: {

    getErrorMessages (validator) { return validator?.$errors.map(e => e.$message) },

    async onCardCreate () {
      return this.peDetentionHistoriesCardCreate({ index: this.sectionIdentifier, section: this.section })
    },

    async onCardDelete () {
      if (this.create) return this.peDetentionHistoriesCardCreateUndo()
      else {
        return this.peDetentionHistoriesCardDelete({ id: this.section.id, index: this.sectionIdentifier })
      }
    },

    async onCardRefresh () {
      return this.peDetentionHistoriesCardRefresh({ index: this.sectionIdentifier, section: this.section })
    },

    async onCardUpdate () {
      return this.peDetentionHistoriesCardUpdate({
        id: this.section.id,
        index: this.sectionIdentifier,
        section: this.section,
      })
    },

    onInitiatedDateUncertainUpdate (value) {
      this.v$.section.initiated_date.$touch()
      this.v$.section.initiated_date_uncertain.$touch()
      this.section.initiated_date_uncertain = value
    },

    onInitiatedDateUpdate (value) {
      this.v$.section.initiated_date.$touch()
      this.v$.section.initiated_date_uncertain.$touch()
      this.section.initiated_date = value.date
      this.section.initiated_date_format = value.format
    },

    async onSectionUnlock () {
      return this.setPeDetentionHistoriesCardLock({ index: this.sectionIdentifier, locked: false })
    },

    // on-terminated_date update
    onTerminatedDateUncertainUpdate (value) {
      this.v$.section.terminated_date.$touch()
      this.v$.section.terminated_date_uncertain.$touch()
      this.section.terminated_date_uncertain = value
    },

    onTerminatedDateUpdate (value) {
      this.v$.section.terminated_date.$touch()
      this.v$.section.terminated_date_uncertain.$touch()
      this.section.terminated_date = value.date
      this.section.terminated_date_format = value.format
    },

    async submitForm () {
      if (this.locked) return this.onSectionUnlock()
      else if (this.create) return this.onCardCreate()
      else return this.onCardUpdate()
    },

    ...mapMutations(PARTY_NAMESPACE, [
      'peDetentionHistoriesCardCreateUndo',
      'setPeDetentionHistoriesCardLock',

    ]),

    ...mapActions(PARTY_NAMESPACE, [
      'initPeDetentionHistoriesCard',
      'peDetentionHistoriesCardCreate',
      'peDetentionHistoriesCardDelete',
      'peDetentionHistoriesCardRefresh',
      'peDetentionHistoriesCardUpdate',
    ]),

    ...mapActions(SHARED_NAMESPACE, [
      'getDropdownOptions',
    ]),
  },
}
</script>
